// "Send extra box" — one more parcel on an order that has already shipped.
//
// Poland finds units after their order has gone out (Alex, 2026-10-22).
// Rather than a replacement order (which would be picked from stock), the
// found unit goes out in an EXTRA BOX on the same order: its own courier
// label, recorded as another parcel so the conveyor unload, courier pallets
// and Load lorry track it like every other box. Nothing else about the
// order changes — no status, no stock, no channel push; the customer gets
// an "extra parcel" email.
//
// A unit whose refund is still PENDING (Orders › Pending Refund) can go too:
// choosing more than the line's free units turns the button into "Cancel the
// pending refund and send", and the BE takes those units off the refund when
// the box is recorded (2026-10-23, per Alex) — so a found item never ships
// AND gets refunded.
//
// The operator ticks what goes in the box (capped at ordered − refunded; an
// item may go out twice — the first box may have shipped without it), picks
// the box (its size goes on the label), and the courier service (defaults to
// the one the order shipped with). The contents weight comes from the
// product weights; when a product has none the weight must be typed.
//
// Scanning an item adds it (2026-10-23, per Alex — finding the line by eye
// on a long order was too slow): the scan is matched against each line's
// sku / barcode / master sku / alternate barcodes, the line is ticked, and
// each further scan adds one more unit up to its cap. Keystrokes typed into
// the weight / size fields are left alone.
//
// Book & print books the parcel, polls until the label is ready (the BE
// records the parcel the first time it is), then prints through the same
// cascade as a reprint from the Shipment table.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Loader2, Minus, PackagePlus, Plus, Printer, ScanLine, X } from "lucide-react";
import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import { playError, playSuccess } from "../helpers/audioPlayer";
import DispatchPrinterButton from "./dispatch-printer-button";
import { useParcelLabels } from "./parcel-label-actions";
import type { DispatchOrder, ExtraBoxDraft, ExtraBoxDraftLine, Parcel } from "../../types/Dispatch";
import "../../styles/extra-box.css";

type Props = {
    order: DispatchOrder;
    onClose: () => void;
    // Fires once a parcel has been recorded, so the Orders page reloads the
    // order (the new box joins the Shipment table).
    onRecorded: () => void;
};

type Selection = Map<number, number>;

type Phase =
    | { kind: "form" }
    | { kind: "booking" }
    | { kind: "waiting"; bookingId: number }
    | { kind: "stalled"; bookingId: number }
    | { kind: "done"; trackingNumbers: string[]; boxPosition: number | null };

const CUSTOM_BOX = "__custom__";
// The label is normally ready within a few seconds; after this the dialog
// stops polling and offers Finish again rather than spinning forever.
const POLL_ATTEMPTS = 15;
const POLL_MS = 2000;

// A scanner types the whole code inside a few hundred ms; a buffer left
// idle longer than this is a stray keypress and is dropped.
const SCAN_IDLE_MS = 1000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Text the operator types themselves — never treat it as a scan.
const isTypingTarget = (el: EventTarget | null) => {
    if (!(el instanceof HTMLElement)) return false;
    if (el instanceof HTMLTextAreaElement || el.isContentEditable) return true;
    if (el instanceof HTMLInputElement) return !["checkbox", "radio", "button", "submit"].includes(el.type);
    return false;
};

// Free units plus any whose refund is still pending (sending those
// cancels their refund).
const capOf = (line: ExtraBoxDraftLine) => line.max + (line.pending ?? 0);

const lineCodes =(line: ExtraBoxDraftLine) => (line.barcodes && line.barcodes.length > 0 ? line.barcodes : [line.sku]);

// The first line (with room left) whose codes include the scan. Exact match
// first — one supplier ships case-significant barcodes — then any case.
export const matchScannedLine = (lines: ExtraBoxDraftLine[], code: string, hasRoom: (l: ExtraBoxDraftLine) => boolean) => {
    const exact = lines.filter((l) => lineCodes(l).includes(code));
    const lower = code.toLowerCase();
    const hits = exact.length > 0 ? exact : lines.filter((l) => lineCodes(l).some((c) => c.toLowerCase() === lower));
    return hits.find(hasRoom) ?? hits[0] ?? null;
};

type ScanFeedback = { ok: boolean; text: string };

const ExtraBoxModal = ({ order, onClose, onRecorded }: Props) => {
    const { t } = useTranslation();
    const [draft, setDraft] = useState<ExtraBoxDraft | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [selection, setSelection] = useState<Selection>(new Map());
    const [boxBarcode, setBoxBarcode] = useState<string>("");
    const [custom, setCustom] = useState({ length_mm: "", width_mm: "", height_mm: "" });
    const [weightInput, setWeightInput] = useState("");
    const [serviceId, setServiceId] = useState<number | null>(null);
    const [phase, setPhase] = useState<Phase>({ kind: "form" });
    const [error, setError] = useState<string | null>(null);
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);

    const labels = useParcelLabels(order.order_id, []);

    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/orders/${encodeURIComponent(order.order_id)}/extra-box/draft`, {});
                const data = await res.json().catch(() => ({}));
                if (!alive.current) return;
                if (!res.ok) { setLoadError(data?.message || t("extra_box_draft_failed")); return; }
                const d = data as ExtraBoxDraft;
                setDraft(d);
                setServiceId(d.default_service_id);
                if (d.boxes.length > 0) setBoxBarcode(d.boxes[0].barcode);
                else setBoxBarcode(CUSTOM_BOX);
                if (d.pending_booking_id) setPhase({ kind: "stalled", bookingId: d.pending_booking_id });
            } catch {
                if (alive.current) setLoadError(t("extra_box_draft_failed"));
            }
        })();
    }, [order.order_id, t]);

    const toggle = (line: ExtraBoxDraftLine) => {
        setSelection((prev) => {
            const next = new Map(prev);
            if (next.has(line.line_position)) next.delete(line.line_position);
            else next.set(line.line_position, 1);
            return next;
        });
    };
    const step = (line: ExtraBoxDraftLine, delta: number) => {
        setSelection((prev) => {
            const current = prev.get(line.line_position);
            if (current === undefined) return prev;
            const next = new Map(prev);
            next.set(line.line_position, Math.min(capOf(line), Math.max(1, current + delta)));
            return next;
        });
    };

    // ── Scan to add ──────────────────────────────────────────────────────
    const [scanFeedback, setScanFeedback] = useState<ScanFeedback | null>(null);
    const [flashLine, setFlashLine] = useState<number | null>(null);
    const selectionRef = useRef(selection);
    selectionRef.current = selection;

    const handleScan = useCallback((raw: string) => {
        const code = raw.trim();
        if (!code || !draft) return;
        const current = selectionRef.current;
        const hasRoom = (l: ExtraBoxDraftLine) => (current.get(l.line_position) ?? 0) < capOf(l);
        const line = matchScannedLine(draft.lines, code, hasRoom);
        const name = line ? (line.title || line.sku) : "";
        if (!line) {
            playError();
            setScanFeedback({ ok: false, text: t("extra_box_scan_not_on_order", { code }) });
            return;
        }
        if (!hasRoom(line)) {
            playError();
            setScanFeedback({ ok: false, text: t("extra_box_scan_full", { item: name, n: String(capOf(line)) }) });
            return;
        }
        const qty = (current.get(line.line_position) ?? 0) + 1;
        setSelection((prev) => new Map(prev).set(line.line_position, qty));
        playSuccess();
        setScanFeedback({ ok: true, text: t("extra_box_scan_added", { item: name, n: String(qty) }) });
        setFlashLine(line.line_position);
        document.getElementById(`extra-box-line-${line.line_position}`)?.scrollIntoView?.({ block: "nearest" });
    }, [draft, t]);

    // The listener is attached once for the dialog's life and reads the
    // latest handler / state through refs, so a scan straight after the
    // draft arrives is never missed while a re-subscribe is pending.
    const scanEnabledRef = useRef(false);
    scanEnabledRef.current = !!draft && phase.kind === "form";
    const handleScanRef = useRef(handleScan);
    handleScanRef.current = handleScan;
    useEffect(() => {
        let buffer = "";
        let last = 0;
        const onKey = (e: KeyboardEvent) => {
            if (!scanEnabledRef.current) return;
            if (e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return;
            const now = Date.now();
            if (now - last > SCAN_IDLE_MS) buffer = "";
            last = now;
            if (e.key === "Enter") {
                if (!buffer) return;
                e.preventDefault();
                const code = buffer;
                buffer = "";
                handleScanRef.current(code);
                return;
            }
            if (e.key.length !== 1) return;
            // Keep a focused select / checkbox from reacting to the scan.
            e.preventDefault();
            buffer += e.key;
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    useEffect(() => {
        if (flashLine === null) return;
        const id = setTimeout(() => setFlashLine(null), 1200);
        return () => clearTimeout(id);
    }, [flashLine, scanFeedback]);

    // Lines the desk marked missing come first — they are what the box is
    // usually for, and on a long order they were hard to find (2026-10-29).
    const orderedLines = useMemo(() => {
        const lines = draft?.lines ?? [];
        return [...lines.filter((l) => (l.missing ?? 0) > 0), ...lines.filter((l) => !((l.missing ?? 0) > 0))];
    }, [draft]);

    const chosenLines = useMemo(
        () => (draft?.lines ?? []).filter((l) => selection.has(l.line_position)),
        [draft, selection],
    );
    const units = useMemo(() => [...selection.values()].reduce((s, n) => s + n, 0), [selection]);
    // Units chosen past each line's free count — their pending refund is
    // cancelled when the box is recorded.
    const cancelUnits = useMemo(
        () => chosenLines.reduce((s, l) => s + Math.max(0, (selection.get(l.line_position) ?? 0) - l.max), 0),
        [chosenLines, selection],
    );
    // Contents weight from the product weights; null when a chosen product
    // has no weight on file (the operator must type it).
    const computedGrams = useMemo(() => {
        if (chosenLines.length === 0) return 0;
        if (chosenLines.some((l) => !l.weight_grams)) return null;
        return chosenLines.reduce((s, l) => s + (l.weight_grams ?? 0) * (selection.get(l.line_position) ?? 0), 0);
    }, [chosenLines, selection]);
    const typedGrams = Number(weightInput) > 0 ? Math.round(Number(weightInput)) : null;
    const weightGrams = typedGrams ?? computedGrams;

    const box = useMemo(() => {
        if (boxBarcode === CUSTOM_BOX) {
            const mm = (v: string) => (Number(v) > 0 ? Math.round(Number(v)) : null);
            return { barcode: null, name: null, length_mm: mm(custom.length_mm), width_mm: mm(custom.width_mm), height_mm: mm(custom.height_mm), tare_grams: null };
        }
        return draft?.boxes.find((b) => b.barcode === boxBarcode) ?? null;
    }, [boxBarcode, custom, draft]);
    const boxOk = !!box && !!box.length_mm && !!box.width_mm && !!box.height_mm;

    const canBook = phase.kind === "form" && selection.size > 0 && !!weightGrams && weightGrams > 0 && boxOk && serviceId !== null;

    const printTracking = useCallback(async (trackingNumbers: string[], carrier: string) => {
        for (const tracking of trackingNumbers) {
            const parcel: Parcel = { tracking_number: tracking, carrier, items: [], label_available: true };
            await labels.printParcel(parcel);
        }
    }, [labels]);

    const carrierName = useMemo(
        () => draft?.services.find((s) => s.service_id === serviceId)?.carrier_name ?? "",
        [draft, serviceId],
    );

    // Poll the finish endpoint until the parcel is recorded.
    const finish = useCallback(async (bookingId: number) => {
        setError(null);
        setPhase({ kind: "waiting", bookingId });
        for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt += 1) {
            let data: Record<string, unknown> = {};
            try {
                const res = await apiFetch(
                    `/orders/${encodeURIComponent(order.order_id)}/extra-box/${bookingId}/finish`,
                    { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" },
                );
                data = await res.json().catch(() => ({}));
                if (!res.ok) {
                    if (!alive.current) return;
                    setError((data.message as string) || t("extra_box_failed"));
                    setPhase({ kind: "stalled", bookingId });
                    return;
                }
            } catch {
                if (!alive.current) return;
                setError(t("extra_box_failed"));
                setPhase({ kind: "stalled", bookingId });
                return;
            }
            if (!alive.current) return;
            if (data.status === "recorded") {
                const trackingNumbers = Array.isArray(data.tracking_numbers) ? (data.tracking_numbers as string[]) : [];
                setPhase({ kind: "done", trackingNumbers, boxPosition: (data.box_position as number) ?? null });
                onRecorded();
                const booking = data.booking as { carrierName?: string } | undefined;
                await printTracking(trackingNumbers, booking?.carrierName ?? carrierName);
                return;
            }
            await sleep(POLL_MS);
            if (!alive.current) return;
        }
        setError(t("extra_box_label_slow"));
        setPhase({ kind: "stalled", bookingId });
    }, [order.order_id, onRecorded, printTracking, carrierName, t]);

    const book = async () => {
        if (!canBook || !box) return;
        setError(null);
        setPhase({ kind: "booking" });
        try {
            const res = await apiFetch(`/orders/${encodeURIComponent(order.order_id)}/extra-box`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    lines: [...selection.entries()].map(([line_position, quantity]) => ({ line_position, quantity })),
                    box,
                    weight_grams: typedGrams,
                    service_id: serviceId,
                    ...(cancelUnits > 0 ? { cancel_pending_refund: true } : {}),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!alive.current) return;
            if (res.status === 409 && data?.code === "extra_box_pending" && data?.booking?.id) {
                // A box booked earlier was never finished — finish that one.
                await finish(Number(data.booking.id));
                return;
            }
            if (!res.ok || !data?.booking?.id) {
                setError(data?.message || t("extra_box_failed"));
                setPhase({ kind: "form" });
                return;
            }
            await finish(Number(data.booking.id));
        } catch {
            if (!alive.current) return;
            setError(t("extra_box_failed"));
            setPhase({ kind: "form" });
        }
    };

    const busy = phase.kind === "booking" || phase.kind === "waiting";
    const boxNo = draft?.next_box ?? null;

    return (
        <div className="modal-overlay" onClick={() => { if (!busy) onClose(); }}>
            <div className="modal-card dispatch-replacement-modal dispatch-extra-box-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2>{t("extra_box_title")}</h2>
                    <button className="modal-close" onClick={onClose} disabled={busy} aria-label={t("cancel")}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    {loadError && <p className="dispatch-replacement-error">{loadError}</p>}
                    {!draft && !loadError && (
                        <p className="dispatch-extra-box-status"><Loader2 size={14} className="spin" /> {t("loading")}</p>
                    )}

                    {draft && phase.kind === "form" && (
                        <>
                            <p className="dispatch-replacement-intro">
                                {t("extra_box_intro", { order: order.order_number, n: String(boxNo ?? "") })}
                            </p>

                            {draft.lines.length > 0 && (
                                <p
                                    className={`dispatch-extra-box-scan${scanFeedback ? (scanFeedback.ok ? " is-ok" : " is-error") : ""}`}
                                    role="status"
                                    data-testid="extra-box-scan-feedback"
                                >
                                    <ScanLine size={14} />
                                    {scanFeedback ? scanFeedback.text : t("extra_box_scan_hint")}
                                </p>
                            )}

                            {draft.lines.length === 0 ? (
                                <p className="dispatch-replacement-error">{t("extra_box_no_lines")}</p>
                            ) : (
                                <ul className="dispatch-replacement-lines">
                                    {orderedLines.map((line) => {
                                        const chosen = selection.get(line.line_position);
                                        const selected = chosen !== undefined;
                                        const missing = line.missing ?? 0;
                                        return (
                                            <li
                                                key={line.line_position}
                                                id={`extra-box-line-${line.line_position}`}
                                                data-testid={missing > 0 ? "extra-box-missing-line" : undefined}
                                                className={`dispatch-replacement-line${missing > 0 ? " dispatch-extra-box-line-missing" : ""}${flashLine === line.line_position ? " dispatch-extra-box-line-flash" : ""}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selected}
                                                    onChange={() => toggle(line)}
                                                    aria-label={line.title || line.sku}
                                                />
                                                <span className="dispatch-replacement-info">
                                                    {missing > 0 && (
                                                        <span className="dispatch-extra-box-missing-tag">
                                                            <AlertTriangle size={12} />
                                                            {t("extra_box_line_missing", { n: String(missing) })}
                                                        </span>
                                                    )}
                                                    <span className="dispatch-replacement-title">{line.title || line.sku}</span>
                                                    <span className="dispatch-replacement-sku">{line.sku}</span>
                                                    <span className="dispatch-replacement-stock">
                                                        {line.refunded > 0
                                                            ? t("extra_box_line_refunded", { ordered: String(line.quantity), refunded: String(line.refunded) })
                                                            : t("extra_box_line_ordered", { ordered: String(line.quantity) })}
                                                        {(line.pending ?? 0) > 0 && ` · ${t("extra_box_line_refund_pending", { n: String(line.pending) })}`}
                                                        {!line.weight_grams && ` · ${t("extra_box_no_weight")}`}
                                                    </span>
                                                </span>
                                                <span className="dispatch-replacement-stepper">
                                                    <button type="button" onClick={() => step(line, -1)} disabled={!selected || (chosen ?? 0) <= 1} aria-label="-1">
                                                        <Minus size={16} />
                                                    </button>
                                                    <span className="dispatch-replacement-qty">{chosen ?? 0}</span>
                                                    <button type="button" onClick={() => step(line, 1)} disabled={!selected || (chosen ?? 0) >= capOf(line)} aria-label="+1">
                                                        <Plus size={16} />
                                                    </button>
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}

                            <div className="dispatch-extra-box-fields">
                                <label className="dispatch-extra-box-field">
                                    <span className="dispatch-replacement-label">{t("extra_box_box")}</span>
                                    <select value={boxBarcode} onChange={(e) => setBoxBarcode(e.target.value)} aria-label={t("extra_box_box")}>
                                        {draft.boxes.map((b) => (
                                            <option key={b.barcode} value={b.barcode}>
                                                {b.name}{b.length_mm && b.width_mm && b.height_mm ? ` · ${b.length_mm}×${b.width_mm}×${b.height_mm} mm` : ""}
                                            </option>
                                        ))}
                                        <option value={CUSTOM_BOX}>{t("extra_box_custom_box")}</option>
                                    </select>
                                </label>
                                {boxBarcode === CUSTOM_BOX && (
                                    <div className="dispatch-extra-box-dims">
                                        {(["length_mm", "width_mm", "height_mm"] as const).map((k) => (
                                            <input
                                                key={k}
                                                type="number"
                                                min={1}
                                                inputMode="numeric"
                                                placeholder={t(`extra_box_${k}`)}
                                                aria-label={t(`extra_box_${k}`)}
                                                value={custom[k]}
                                                onChange={(e) => setCustom((c) => ({ ...c, [k]: e.target.value }))}
                                            />
                                        ))}
                                    </div>
                                )}

                                <label className="dispatch-extra-box-field">
                                    <span className="dispatch-replacement-label">{t("extra_box_weight")}</span>
                                    <input
                                        type="number"
                                        min={1}
                                        inputMode="numeric"
                                        aria-label={t("extra_box_weight")}
                                        placeholder={computedGrams ? String(computedGrams) : t("extra_box_weight_required")}
                                        value={weightInput}
                                        onChange={(e) => setWeightInput(e.target.value)}
                                    />
                                    <span className="admin-settings-section-hint">
                                        {computedGrams === null && selection.size > 0
                                            ? t("extra_box_weight_missing")
                                            : t("extra_box_weight_hint", { kg: ((weightGrams ?? 0) / 1000).toFixed(2) })}
                                    </span>
                                </label>

                                <label className="dispatch-extra-box-field">
                                    <span className="dispatch-replacement-label">{t("extra_box_courier")}</span>
                                    <select
                                        value={serviceId ?? ""}
                                        onChange={(e) => setServiceId(e.target.value ? Number(e.target.value) : null)}
                                        aria-label={t("extra_box_courier")}
                                    >
                                        {draft.services.length === 0 && <option value="">{t("extra_box_no_services")}</option>}
                                        {draft.services.map((s) => (
                                            <option key={s.service_id} value={s.service_id}>
                                                {s.carrier_name} · {s.service_name}
                                                {s.service_id === draft.default_service_id ? ` (${t("extra_box_original")})` : ""}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>

                            {cancelUnits > 0 && (
                                <p className="dispatch-extra-box-refund-note" data-testid="extra-box-refund-note">
                                    {t("extra_box_cancel_refund_note", { n: String(cancelUnits) })}
                                </p>
                            )}
                            <p className="dispatch-flagged-action-result">{t("extra_box_rules_note")}</p>
                        </>
                    )}

                    {(phase.kind === "booking" || phase.kind === "waiting") && (
                        <p className="dispatch-extra-box-status">
                            <Loader2 size={14} className="spin" />{" "}
                            {phase.kind === "booking" ? t("extra_box_booking", { carrier: carrierName }) : t("extra_box_waiting_label")}
                        </p>
                    )}

                    {phase.kind === "stalled" && (
                        <p className="dispatch-extra-box-status">{t("extra_box_pending_note")}</p>
                    )}

                    {phase.kind === "done" && (
                        <div className="dispatch-extra-box-done">
                            <p>
                                {t("extra_box_done", {
                                    n: String(phase.boxPosition ?? boxNo ?? ""),
                                    tracking: phase.trackingNumbers.join(", "),
                                })}
                            </p>
                        </div>
                    )}

                    {error && <p className="dispatch-replacement-error" role="alert">{error}</p>}

                    <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                        {phase.kind !== "done" && (
                            <button className="btn btn-ghost dispatch-modal-btn" onClick={onClose} disabled={busy}>
                                {t("cancel")}
                            </button>
                        )}
                        {phase.kind === "form" && draft && (
                            <>
                                <DispatchPrinterButton onModeChange={labels.detectPrinter} />
                                <button className="btn btn-primary dispatch-modal-btn" onClick={book} disabled={!canBook}>
                                    <PackagePlus size={14} />
                                    {cancelUnits > 0
                                        ? t("extra_box_book_cancel_refund", { n: String(boxNo ?? ""), units: String(units) })
                                        : t("extra_box_book", { n: String(boxNo ?? ""), units: String(units) })}
                                </button>
                            </>
                        )}
                        {phase.kind === "stalled" && (
                            <button className="btn btn-primary dispatch-modal-btn" onClick={() => finish(phase.bookingId)}>
                                <PackagePlus size={14} />
                                {t("extra_box_finish")}
                            </button>
                        )}
                        {phase.kind === "done" && (
                            <>
                                <button
                                    className="btn btn-secondary dispatch-modal-btn"
                                    onClick={() => printTracking(phase.trackingNumbers, carrierName)}
                                    disabled={labels.busy !== null}
                                >
                                    {labels.busy !== null ? <Loader2 size={14} className="spin" /> : <Printer size={14} />}
                                    {t("extra_box_print_again")}
                                </button>
                                <button className="btn btn-primary dispatch-modal-btn" onClick={onClose}>
                                    {t("extra_box_close")}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExtraBoxModal;
